import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart3, Shield, Globe, Zap } from 'lucide-react';

const AuthPage = () => {
  const { login, register } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '', 
    fullName: '' 
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(loginData.email, loginData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    if (registerData.password !== registerData.confirmPassword) {
      setError(language === 'tr' ? 'Şifreler eşleşmiyor' : 'Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(registerData.email, registerData.password, registerData.fullName);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="fixed inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)`
        }} />
      </div>
      
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 relative z-10">
        {/* Left side - Branding */}
        <div className="hidden lg:flex flex-col justify-center space-y-8 p-8">
          <div>
            <h1 className="text-4xl font-bold text-white tracking-tight mb-4">
              GDMII
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              {language === 'tr' 
                ? 'Global Dijital Olgunluk Değerlendirme Endeksi - Şirketinizin dijital dönüşüm seviyesini bilimsel metodoloji ile ölçün.'
                : 'Global Digital Maturity Intelligence Index - Measure your company\'s digital transformation level with scientific methodology.'}
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-4 text-slate-300">
              <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400">
                <BarChart3 size={24} />
              </div>
              <div>
                <h3 className="font-semibold">320+ {language === 'tr' ? 'Değerlendirme Sorusu' : 'Assessment Questions'}</h3>
                <p className="text-sm text-slate-500">{language === 'tr' ? '6 stratejik alan' : '6 strategic pillars'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-slate-300">
              <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400">
                <Zap size={24} />
              </div>
              <div>
                <h3 className="font-semibold">{language === 'tr' ? 'AI Destekli Raporlar' : 'AI-Powered Reports'}</h3>
                <p className="text-sm text-slate-500">{language === 'tr' ? 'Kurumsal seviye' : 'Enterprise-grade'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-slate-300">
              <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                <Globe size={24} />
              </div>
              <div>
                <h3 className="font-semibold">{language === 'tr' ? 'Global Benchmark' : 'Global Benchmarking'}</h3>
                <p className="text-sm text-slate-500">{language === 'tr' ? 'Sektör karşılaştırması' : 'Industry comparison'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-slate-300">
              <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400">
                <Shield size={24} />
              </div>
              <div>
                <h3 className="font-semibold">{language === 'tr' ? 'GDPR Uyumlu' : 'GDPR Compliant'}</h3>
                <p className="text-sm text-slate-500">{language === 'tr' ? 'Kurumsal güvenlik' : 'Enterprise security'}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right side - Auth form */}
        <div className="flex items-center justify-center">
          <Card className="w-full max-w-md bg-slate-900/80 border-slate-800 backdrop-blur-xl" data-testid="auth-card">
            <CardHeader className="space-y-1 text-center">
              <div className="flex justify-between items-center mb-4">
                <Link to="/" className="text-2xl font-bold text-white">GDMII</Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleLanguage}
                  className="text-slate-400 hover:text-white"
                  data-testid="language-toggle"
                >
                  {language === 'tr' ? 'EN' : 'TR'}
                </Button>
              </div>
              <CardTitle className="text-2xl text-white">
                {language === 'tr' ? 'Hoş Geldiniz' : 'Welcome'}
              </CardTitle>
              <CardDescription className="text-slate-400">
                {language === 'tr' 
                  ? 'Dijital olgunluk değerlendirmenize başlayın'
                  : 'Start your digital maturity assessment'}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-slate-800">
                  <TabsTrigger value="login" data-testid="login-tab">{t('login')}</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">{t('register')}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-slate-300">{t('email')}</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                        placeholder="ornek@sirket.com"
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-slate-300">{t('password')}</Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                        placeholder="••••••••"
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                    
                    {error && (
                      <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? t('loading') : t('login')}
                    </Button>
                    
                    <div className="text-center">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-blue-400 hover:text-blue-300"
                        data-testid="forgot-password-link"
                      >
                        {language === 'tr' ? 'Şifremi Unuttum' : 'Forgot Password?'}
                      </Link>
                    </div>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-slate-300">{t('fullName')}</Label>
                      <Input
                        id="register-name"
                        type="text"
                        value={registerData.fullName}
                        onChange={(e) => setRegisterData({ ...registerData, fullName: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                        placeholder="Ahmet Yılmaz"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-slate-300">{t('email')}</Label>
                      <Input
                        id="register-email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                        placeholder="ornek@sirket.com"
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-slate-300">{t('password')}</Label>
                      <Input
                        id="register-password"
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                        placeholder="••••••••"
                        required
                        minLength={8}
                        data-testid="register-password-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm" className="text-slate-300">{t('confirmPassword')}</Label>
                      <Input
                        id="register-confirm"
                        type="password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                        placeholder="••••••••"
                        required
                        minLength={8}
                        data-testid="register-confirm-input"
                      />
                    </div>
                    
                    {error && (
                      <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? t('loading') : t('register')}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
